import { createCallEnvelope, RPCError, type Envelope } from '@geist/rpc-core';

declare global {
  // eslint-disable-next-line no-var
  var __DRAHT_DEVTOOLS__:
    | {
        onClientEnvelope?: (direction: 'send' | 'recv', envelope: unknown) => void;
      }
    | undefined;

  interface Window {
    __TAURI_INTERNALS__?: unknown;
  }
}

export interface ITransport {
  readonly readyState: number;
  onopen: (() => void) | null;
  onmessage: ((evt: { data: unknown }) => void) | null;
  onclose: (() => void) | null;
  onerror: ((evt: Event | Error | unknown) => void) | null;
  send(data: string): void;
  close(): void;
}

import { TauriTransport } from './transports/tauri.js';

export interface DrahtClientOptions {
  url: string;
  token: string | null;
  /**
   * Async token factory — called fresh on every (re)connect.
   * Takes precedence over `token` when provided.
   * Use this for Ed25519-signed tokens that expire (maxSkewMs).
   */
  getToken?: (() => Promise<string | null>) | null;
  onConnected: (() => void) | null;
  onDisconnected: (() => void) | null;
  onConnectError?: ((error: Error) => void) | null;
  /** After this many ms of continuous failed reconnects, stop and fire onFailed. Default: null (loop forever). */
  failAfterMs?: number | null;
  /** Called when failAfterMs elapses without a successful connection. */
  onFailed?: ((reason: string) => void) | null;
}

interface PendingCall {
  resolve: (data: unknown) => void;
  reject: (err: Error) => void;
  timer: ReturnType<typeof setTimeout>;
}

export type DrahtParser<T> = { parse: (input: unknown) => T };

export class DrahtClient {
  private ws: ITransport | null = null;
  private pending = new Map<string, PendingCall>();
  private listeners = new Map<string, Set<(data: unknown) => void>>();
  private tunnelHandlers = new Map<string, (buf: Uint8Array) => void>();
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private failTimer: ReturnType<typeof setTimeout> | null = null;
  private closed = false;
  private hasFailed = false;

  constructor(private readonly options: DrahtClientOptions) {}

  connect(): void {
    this.closed = false;
    this.hasFailed = false;
    this._startFailTimer();
    this._connect();
  }

  disconnect(): void {
    this.closed = true;
    this._clearTimers();
    if (this.ws !== null) {
      this.ws.close();
      this.ws = null;
    }
  }

  reconnect(): void {
    this.disconnect();
    this.connect();
  }

  call<TOutput = unknown>(
    method: string,
    data: unknown,
    schema?: DrahtParser<TOutput>,
  ): Promise<TOutput> {
    return new Promise((resolve, reject) => {
      if (this.ws === null || this.ws.readyState !== WebSocket.OPEN) {
        reject(new Error('not connected'));
        return;
      }
      const envelope = createCallEnvelope(method, data);
      const timer = setTimeout(() => {
        this.pending.delete(envelope.id);
        reject(new RPCError(-32001, `Request timeout: ${method}`));
      }, 30_000);
      const schemaToUse: DrahtParser<TOutput> = schema ?? { parse: (raw) => raw as TOutput };
      this.pending.set(envelope.id, {
        resolve: (raw) => resolve(schemaToUse.parse(raw)),
        reject,
        timer,
      });
      globalThis.__DRAHT_DEVTOOLS__?.onClientEnvelope?.('send', envelope);
      this.ws.send(JSON.stringify(envelope));
    });
  }

  /** Send an RPC call and receive the raw binary response as a Blob. */
  callBinary(method: string, data: unknown): Promise<Blob> {
    return new Promise((resolve, reject) => {
      if (this.ws === null || this.ws.readyState !== WebSocket.OPEN) {
        reject(new Error('not connected'));
        return;
      }
      const envelope = createCallEnvelope(method, data);
      const timer = setTimeout(() => {
        this.pending.delete(envelope.id);
        reject(new RPCError(-32001, `Request timeout: ${method}`));
      }, 30_000);
      this.pending.set(envelope.id, {
        resolve: (raw) => resolve(raw as Blob),
        reject,
        timer,
      });
      globalThis.__DRAHT_DEVTOOLS__?.onClientEnvelope?.('send', envelope);
      this.ws.send(JSON.stringify(envelope));
    });
  }

  /** Subscribe to raw binary tunnel frames identified by `proc`. Returns an unsubscribe fn. */
  tunnel(proc: string, handler: (buf: Uint8Array) => void): () => void {
    this.tunnelHandlers.set(proc, handler);
    return () => {
      this.tunnelHandlers.delete(proc);
    };
  }

  on(event: string, cb: (data: unknown) => void): () => void {
    let set = this.listeners.get(event);
    if (set === undefined) {
      set = new Set();
      this.listeners.set(event, set);
    }
    set.add(cb);
    return () => {
      this.listeners.get(event)?.delete(cb);
    };
  }

  send(envelope: Envelope): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      globalThis.__DRAHT_DEVTOOLS__?.onClientEnvelope?.('send', envelope);
      this.ws.send(JSON.stringify(envelope));
    }
  }

  private _connect(): void {
    let onOpenFired = false;
    let connectTimeout: ReturnType<typeof setTimeout> | null = null;

    let ws: ITransport;
    if (globalThis.window?.__TAURI_INTERNALS__) {
      ws = new TauriTransport();
    } else {
      ws = new WebSocket(this.options.url) as unknown as ITransport;
    }

    this.ws = ws;
    connectTimeout = setTimeout(() => {
      if (!onOpenFired) {
        ws.close();
        this.options.onConnectError?.(new Error('WebSocket connection timeout (10s)'));
      }
    }, 10_000);

    ws.onopen = () => {
      onOpenFired = true;
      if (connectTimeout !== null) {
        clearTimeout(connectTimeout);
        connectTimeout = null;
      }
      this._startPing();
      // Cancel the fail timer — we got a successful WebSocket open
      if (this.failTimer !== null) {
        clearTimeout(this.failTimer);
        this.failTimer = null;
      }
      // Await auth.login BEFORE firing onConnected so terminal.create never
      // races ahead of authentication on the daemon side.
      void (async () => {
        let tokenToUse: string | null = null;
        try {
          tokenToUse =
            this.options.getToken != null ? await this.options.getToken() : this.options.token;
        } catch {
          // token factory failed — proceed without auth (open mode still works)
        }
        if (tokenToUse !== null) {
          try {
            await this.call('auth.login', { token: tokenToUse }, { parse: (raw) => raw });
          } catch (loginErr) {
            const msg = (loginErr as Error).message ?? '';
            // 'unknown actor' means the keypair is not registered — surface this
            // so the UI can redirect to /pair instead of silently failing.
            if (msg.includes('unknown actor')) {
              // Permanently close — don't reconnect, keypair must be re-registered
              this.closed = true;
              this._clearTimers();
              ws.close();
              this.options.onConnectError?.(
                new Error('auth.login: unknown actor — keypair not registered'),
              );
              return;
            }
            // Other login errors (e.g. clock skew) → proceed in open mode
          }
        }
        if (this.options.onConnected !== null) {
          this.options.onConnected();
        }
      })();
    };

    ws.onmessage = (evt) => {
      // Binary frames: [36-byte id][1-byte flag][payload]
      if (evt.data instanceof Blob || evt.data instanceof ArrayBuffer) {
        void (async () => {
          const buffer =
            evt.data instanceof Blob
              ? new Uint8Array(await (evt.data as Blob).arrayBuffer())
              : new Uint8Array(evt.data as ArrayBuffer);
          if (buffer.length < 37) return;
          const id = new TextDecoder().decode(buffer.slice(0, 36)).trim();
          const flag = buffer[36];
          const payload = buffer.slice(37);
          if (flag === 0x01) {
            // callBinary response
            const pending = this.pending.get(id);
            if (pending !== undefined) {
              clearTimeout(pending.timer);
              this.pending.delete(id);
              pending.resolve(new Blob([payload]));
            }
          } else {
            // Tunnel binary frame
            this.tunnelHandlers.get(id)?.(payload);
          }
        })();
        return;
      }
      let envelope: Envelope;
      try {
        envelope = JSON.parse(evt.data as string) as Envelope;
      } catch {
        return;
      }
      this._route(envelope);
    };

    ws.onclose = () => {
      if (connectTimeout !== null) {
        clearTimeout(connectTimeout);
        connectTimeout = null;
      }
      this._clearTimers();
      if (this.options.onDisconnected !== null) {
        this.options.onDisconnected();
      }
      for (const [id, pending] of this.pending) {
        pending.reject(new RPCError(-32000, 'Connection closed'));
        this.pending.delete(id);
      }
      if (!this.closed) {
        this.reconnectTimer = setTimeout(() => this._connect(), 2_000);
      }
    };

    ws.onerror = (_e: unknown) => {
      if (!onOpenFired) {
        this.options.onConnectError?.(new Error('WebSocket connection failed'));
      }
      ws.close();
    };
  }

  private _route(envelope: Envelope): void {
    globalThis.__DRAHT_DEVTOOLS__?.onClientEnvelope?.('recv', envelope);
    if (envelope.type === 'response' || envelope.type === 'error') {
      const pending = this.pending.get(envelope.id);
      if (pending !== undefined) {
        clearTimeout(pending.timer);
        this.pending.delete(envelope.id);
        if (envelope.error !== null) {
          pending.reject(RPCError.fromEnvelopeError(envelope.error));
        } else {
          pending.resolve(envelope.data);
        }
      }
      return;
    }
    if (envelope.type === 'event' && envelope.method !== null) {
      const cbs = this.listeners.get(envelope.method);
      if (cbs !== undefined) {
        for (const cb of cbs) {
          cb(envelope.data);
        }
      }
    }
  }

  private _startPing(): void {
    this.pingTimer = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        const envelope = {
          type: 'system',
          id: 'ping',
          method: 'ping',
          data: null,
          error: null,
          ts: Date.now(),
          pos: null,
        };
        globalThis.__DRAHT_DEVTOOLS__?.onClientEnvelope?.('send', envelope);
        this.ws.send(JSON.stringify(envelope));
      }
    }, 30_000);
  }

  private _clearTimers(): void {
    if (this.pingTimer !== null) {
      clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.failTimer !== null) {
      clearTimeout(this.failTimer);
      this.failTimer = null;
    }
  }

  private _startFailTimer(): void {
    if (this.options.failAfterMs == null) return;
    if (this.failTimer !== null) return; // already running
    this.failTimer = setTimeout(() => {
      this.failTimer = null;
      if (this.closed || this.hasFailed) return;
      this.hasFailed = true;
      this.closed = true;
      this._clearTimers();
      if (this.ws !== null) {
        this.ws.close();
        this.ws = null;
      }
      this.options.onFailed?.('Connection failed after ' + String(this.options.failAfterMs) + 'ms');
    }, this.options.failAfterMs);
  }
}
